
import { defineComponent, ref, computed, toRef, watchEffect, PropType } from 'vue'
import QInput from 'quasar/src/components/input/QInput.js';
import { useFormModule } from '@/modules/_shared/forms'

const { validations: { requiredRule } } = useFormModule()

export default defineComponent({
    name: 'editable-field',
    props: {
        modelValue: {
            type: String,
            required: true
        },
        type: {
            type: String,
            default: 'text'
        },
        isRequired: {
            type: Boolean,
            default: true
        },
        validationRules: {
            type: Array as PropType<Array<(val: string) => string | boolean>>,
            default: () => []
        },
        hint: {
            type: String,
            default: null
        },
        emptyValuePlaceholder: {
            type: String,
            default: null
        }
    },
    emits: ['update:modelValue'],
    setup (props, { emit }) {
        const originalValue = toRef(props, 'modelValue')
        const currentValue = ref(props.modelValue)

        watchEffect(() => {
            currentValue.value = props.modelValue
        })

        const isValid = ref(true)
        const editing = ref(false)
        const inputRef = ref<QInput>()
        const enableEditing = () => {
            if (inputRef.value) {
                inputRef.value.focus()
            }
            editing.value = true
        }

        const disableEditing = () => {
            currentValue.value = originalValue.value
            if (currentValue.value !== '') {
                editing.value = false
            }
        }

        const saveEdit = () => {
            if (inputRef.value?.validate()) {
                editing.value = false
                inputRef.value.blur()
                emitUpdateEvent()
            }
        }

        const onUpdate = async (value: string) => {
            if (inputRef.value) {
                if (!value && !props.isRequired) {
                    isValid.value = true
                } else {
                    isValid.value = await inputRef.value.validate(value)
                }
            }
        }

        const computedPlaceholder = computed(() => {
            if (editing.value) {
                return null
            }
            return props.emptyValuePlaceholder
        })

        const showEditButtons = computed(() => isValid.value && editing.value)
        const emitUpdateEvent = () => emit('update:modelValue', currentValue.value)

        const showBottomHint = computed(() => editing.value && props.hint !== null)
        const inputValidationRules = computed(() => {
            if (!props.isRequired && !currentValue.value) {
                return []
            }
            const rules = props.validationRules
            if (props.isRequired && !rules.includes(requiredRule)) {
                rules.unshift(requiredRule)
            }
            return rules
        })
        return {
            onUpdate,
            originalValue,
            currentValue,
            editing,
            enableEditing,
            disableEditing,
            saveEdit,
            inputRef,
            requiredRule,
            showEditButtons,
            showBottomHint,
            inputValidationRules,
            computedPlaceholder
        }
    }
})
